import type { FC, ReactNode } from 'react'
import type { CmsPage } from '@/types'

import RichText from '@/components/generals/rich-text'
import PageElements from '@/components/landing/page-elements'

interface PageContentProps {
    page: CmsPage
}

// Renders the shared CMS fields of a page: body, quote, contact details, button and note.
const PageContent: FC<PageContentProps> = ({ page }): ReactNode => {
    return (
        <>
            <PageElements page={page} />

            {page.body && <RichText html={page.body} />}

            {page.quote && (
                <blockquote className='flex flex-col gap-4 border-l-2 border-primary pl-6'>
                    <p className='font-heading text-3xl italic leading-snug'>
                        {page.quote}
                    </p>

                    {page.quoteAuthor && (
                        <cite className='text-sm not-italic text-muted-foreground'>
                            {page.quoteAuthor}
                        </cite>
                    )}
                </blockquote>
            )}

            {page.contactDetails && (
                <address className='whitespace-pre-line border-t border-border pt-16 text-lg not-italic'>
                    {page.contactDetails}
                </address>
            )}

            {(page.buttonLabel || page.note) && (
                <div className='flex flex-col items-start gap-4'>
                    {page.buttonLabel && (
                        <a
                            href={page.buttonLink || '/'}
                            className='inline-flex h-12 items-center rounded-full bg-primary px-6 text-base font-medium text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring'
                        >
                            {page.buttonLabel}
                        </a>
                    )}

                    {page.note && (
                        <small className='text-sm text-muted-foreground'>
                            {page.note}
                        </small>
                    )}
                </div>
            )}
        </>
    )
}

export default PageContent
